// components/TaskList.tsx
import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { Task, TaskForSubmission, TrackingProgress } from '../types';
import { TaskItem } from './TaskItem';
import { useUrlValidation } from '../hooks/useUrlValidation';
import { useTimeTracker } from '../hooks/useTimeTracker';

interface TaskListProps {
  tasks: Task[];
  onAddTask: (task: Omit<Task, 'id'>) => void;
  onUpdateTask: (taskId: string, updates: Partial<Task>) => void;
  onDeleteTask: (taskId: string) => void;
  onResetTaskTimes: (taskIds: string[]) => void;
}

export const TaskList: React.FC<TaskListProps> = ({
  tasks,
  onAddTask,
  onUpdateTask,
  onDeleteTask,
  onResetTaskTimes,
}) => {
  const [urlInput, setUrlInput] = useState('');
  const [trackingProgress, setTrackingProgress] = useState<TrackingProgress>({
    status: 'idle',
  });

  const { validateUrl, isValidating, error: validationError } = useUrlValidation();
  const { trackTime, isTracking, error: trackingError } = useTimeTracker();

  const handleAddTask = async () => {
    if (!urlInput.trim()) return;

    try {
      const { name, slug, webPath, projectName } = await validateUrl(urlInput.trim());
      onAddTask({
        name,
        slug,
        webPath,
        timeSpent: 0,
        comment: '',
        projectName,
        isRunning: false,
      });
      setUrlInput('');
    } catch (error) {
      // Ошибка уже обработана в хуке
    }
  };

  const handleUpdateTime = (taskId: string, timeSpent: number) => {
    onUpdateTask(taskId, { timeSpent });
  };

  const handleUpdateComment = (taskId: string, comment: string) => {
    onUpdateTask(taskId, { comment });
  };

  const handleToggleTimer = (taskId: string) => {
    const task = tasks.find((t) => t.id === taskId);
    if (task) {
      // Останавливаем все остальные таймеры
      tasks.forEach((t) => {
        if (t.id !== taskId && t.isRunning) {
          onUpdateTask(t.id, { isRunning: false });
        }
      });

      onUpdateTask(taskId, { isRunning: !task.isRunning });
    }
  };

  const handleTrackTime = async () => {
    try {
      // Подготавливаем данные для отправки (только задачи с потраченным временем)
      const tasksWithTime = tasks.filter((task) => task.timeSpent > 0);

      if (tasksWithTime.length === 0) {
        return;
      }

      const tasksForSubmission: TaskForSubmission[] = tasksWithTime.map((task) => ({
        id: task.id,
        name: task.name,
        slug: task.slug,
        timeSpent: task.timeSpent,
        comment: task.comment,
      }));

      // Получаем ID успешно отправленных задач с отслеживанием прогресса
      const successfullyTrackedTaskIds = await trackTime(
        tasksForSubmission,
        setTrackingProgress,
      );

      // Обнуляем только успешно отправленные задачи
      if (successfullyTrackedTaskIds.length > 0) {
        onResetTaskTimes(successfullyTrackedTaskIds);
      }
    } catch (error) {
      // Ошибка уже обработана в хуке
    }
  };

  // Функция для отображения прогресса трекинга
  const getProgressMessage = (): string => {
    switch (trackingProgress.status) {
      case 'starting':
        return 'Starting tracking time...';
      case 'tracking':
        return `Issue "${trackingProgress.currentTask}" has been tracked (${
          trackingProgress.completedTasks ?? 0 + 1
        }/${trackingProgress.totalTasks})`;
      case 'finished':
        return 'Tracking finished';
      case 'idle':
      default:
        return '';
    }
  };

  // Подсчитываем количество задач с потраченным временем
  const tasksWithTime = tasks.filter((task) => task.timeSpent > 0).length;
  const tasksTime = tasks
    .filter((task) => task.timeSpent > 0)
    .reduce((acc, task) => acc + task.timeSpent, 0);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Задачи
        {tasksWithTime > 0 && (
          <Typography
            component="span"
            variant="body2"
            color="text.secondary"
            sx={{ ml: 1 }}
          >
            ({tasksWithTime} с временем)
          </Typography>
        )}
      </Typography>

      {tasks.map((task) => (
        <TaskItem
          key={task.id}
          task={task}
          onUpdateTime={handleUpdateTime}
          onUpdateComment={handleUpdateComment}
          onToggleTimer={handleToggleTimer}
          onDeleteTask={onDeleteTask}
        />
      ))}

      <Box sx={{ mt: 2, mb: 3 }}>
        <Box display="flex" gap={1} mb={2}>
          <TextField
            fullWidth
            placeholder="Введите URL задачи"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddTask()}
            disabled={isValidating}
            size="small"
          />
          <Button
            variant="contained"
            onClick={handleAddTask}
            disabled={isValidating || !urlInput.trim()}
            startIcon={isValidating ? <CircularProgress size={16} /> : <Add />}
          >
            Добавить
          </Button>
        </Box>

        {validationError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {validationError}
          </Alert>
        )}
      </Box>

      <Button
        variant="contained"
        onClick={handleTrackTime}
        disabled={isTracking || tasksWithTime === 0}
        startIcon={isTracking ? <CircularProgress size={16} /> : undefined}
        fullWidth
      >
        {isTracking
          ? 'Отправка...'
          : `Track Time${
              tasksWithTime > 0 ? ` (~${(tasksTime / 60 / 60).toFixed(1)} ч.)` : ''
            }`}
      </Button>

      {/* Отображение прогресса трекинга */}
      {trackingProgress.status !== 'idle' && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 1, textAlign: 'center' }}
        >
          {getProgressMessage()}
        </Typography>
      )}

      {trackingError && (
        <Alert severity="error" sx={{ mt: 2 }} style={{ whiteSpace: 'pre-line' }}>
          {trackingError}
        </Alert>
      )}
    </Box>
  );
};
